import { useEffect } from 'react';

export function usePageTitle(title) {
  useEffect(() => {
    if (!title) return;
    document.title = title;
    // Basic SEO consumers must not inherit the landing page's social copy.
    _upsertMeta('property', 'og:title', title);
    _upsertMeta('name', 'twitter:title', title);
  }, [title]);
}

// Manage the single <meta name="description"> via direct DOM. We deliberately do
// NOT use react-helmet-async for the description because (a) its tags do not flush
// to the DOM in this CRA dev/preview environment, and (b) on the production build
// it APPENDS a second <meta name="description"> alongside the static one in
// index.html — which triggered the "Duplicate Meta Description Tags" SEO finding.
// Direct DOM (find-or-create) guarantees exactly one description tag, per page,
// in every environment.
export function useMetaDescription(description) {
  useEffect(() => {
    if (!description) return;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', description);
    _upsertMeta('property', 'og:description', description);
    _upsertMeta('name', 'twitter:description', description);
  }, [description]);
}

const _upsertMeta = (attr, key, val) => {
  if (val == null) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', val);
};

// Manage the single set of Open Graph + Twitter social tags via direct DOM (same
// reasoning as useMetaDescription). The base og/twitter tags live in index.html;
// this updates them in place per page so there is never a duplicate on prod.
export function useSocialMeta({ title, description, url, type = 'website' } = {}) {
  useEffect(() => {
    _upsertMeta('property', 'og:type', type);
    _upsertMeta('property', 'og:title', title);
    _upsertMeta('property', 'og:description', description);
    _upsertMeta('property', 'og:url', url);
    _upsertMeta('name', 'twitter:title', title);
    _upsertMeta('name', 'twitter:description', description);
  }, [title, description, url, type]);
}

export function useSeoMeta(title, description) {
  usePageTitle(title);
  useMetaDescription(description);
}

// Keep token-based pages (shared decision packages) out of search results.
// Upserts the single robots tag and restores the previous value on unmount so
// the rest of the SPA stays indexable.
export function useNoIndex() {
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"]');
    const previous = existing ? existing.getAttribute('content') : null;
    _upsertMeta('name', 'robots', 'noindex, nofollow');
    return () => {
      if (previous !== null) _upsertMeta('name', 'robots', previous);
      else document.querySelector('meta[name="robots"]')?.remove();
    };
  }, []);
}
